<!-- Блок фильтров списка с сохраненными наборами и поиском. -->
<template>
  <div class="filter-container" :style="{ marginBottom: bottomMargin }">
    <div class="filter-header">
      <div class="filter-header-toggle" @click="toggleFilters">
        <button
          class="search-button"
          :class="{ active: !isCollapsed }"
        >
          <span class="search-icon">🔍</span>
          <span class="search-label">
            <span>{{ toggleLabel }}</span>
            <span
              v-if="selectedFilterCount > 0"
              class="search-filter-count"
              :aria-label="`Выбрано фильтров: ${selectedFilterCount}`"
            >{{ selectedFilterCount }}</span>
          </span>
          <base-toggle-arrow-button
            class="search-arrow"
            :section-visible="isCollapsed"
            :new-active-color="true"
          />
        </button>
      </div>

      <!-- Сохранённые фильтры -->
      <div
        class="saved-filters"
        v-if="targetType && savedFilters.length > 0"
      >
        <button
          v-for="filter in savedFilters"
          :key="filter.id"
          class="saved-filter-chip"
          :class="{ active: activeFilterId === filter.id }"
          @click="applySavedFilter(filter)"
        >
          <span class="chip-name">{{ filter.name }}</span>
          <span
            class="chip-delete"
            @click.stop="confirmDeleteFilter(filter)"
            title="Удалить фильтр"
          >
            ✕
          </span>
        </button>

        <!-- Кнопка сброса активного сохранённого фильтра -->
        <button
          v-if="activeFilterId"
          class="reset-active-filter-btn"
          @click="handleReset"
          title="Сбросить фильтры"
        >
          Сбросить фильтры
        </button>
      </div>
    </div>

    <transition name="fade-slide">
      <div v-if="!isCollapsed" class="card filter-content">
        <div class="search-fields-grid">
          <div
            v-for="field in searchFields"
            :key="field.value"
            class="filter-group"
            :class="{
              'filter-group--checkbox': field.type === 'checkbox',
            }"
          >
            <base-label
              class="filter-label"
              v-if="field.type !== 'checkbox'"
            >
              {{ field.label }}
            </base-label>

            <!-- Обычный текст -->
            <base-input
              class="search-input"
              v-model="searchValues[field.value]"
              :clearable="true"
              @keydown.enter="handleSearch"
              @update:modelValue="scheduleAutoSearch"
              v-if="field.type == 'input'"
            />

            <!-- Большие числа -->
            <base-input
              class="search-input"
              v-model="searchValues[field.value]"
              type="big_number"
              :clearable="true"
              @keydown.enter="handleSearch"
              @update:modelValue="scheduleAutoSearch"
              v-if="field.type == 'big_number'"
            />

            <!-- Обычный селект -->
            <base-select
              v-model="searchValues[field.value]"
              :options="field.options"
              :clearable="true"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select'"
            />

            <!-- Дата -->
            <base-input
              class="search-input"
              v-model="searchValues[field.value]"
              type="date"
              :clearable="false"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'date'"
            />

            <!-- Выбор контрагента -->
            <CounterpartySelect
              v-model="searchValues[field.value]"
              placeholder="Выберите контрагента"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-counterparty'"
            />

            <!-- Выбор водителя -->
            <DriverSelect
              v-model="searchValues[field.value]"
              placeholder="Выберите водителя"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-driver'"
            />

            <!-- Выбор транспорта -->
            <VehicleSelect
              v-model="searchValues[field.value]"
              placeholder="Выберите транспорт"
              :fleet-park-id="
                searchValues[field.fleetParkField] || null
              "
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-vehicle'"
            />

            <!-- Выбор автопарка -->
            <FleetParkSelect
              v-model="searchValues[field.value]"
              placeholder="Выберите автопарк"
              :with-button="false"
              @update:modelValue="handleFleetParkFilterUpdate(field)"
              v-else-if="field.type == 'select-fleet-park'"
            />

            <!-- Выбор пользователя -->
            <CompanyUserSelect
              v-model="searchValues[field.value]"
              placeholder="Выберите пользователя"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-company-user'"
            />

            <!-- Выбор категории расходов -->
            <ExpenseCategorySelect
              v-model="searchValues[field.value]"
              placeholder="Выберите категорию"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-expense-category'"
            />

            <!-- Выбор источника оплаты -->
            <PaymentSourceSelect
              v-model="searchValues[field.value]"
              :clearable="true"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-payment-source'"
            />

            <!-- Выбор элемента справочника -->
            <ReferenceItemSelect
              v-model="searchValues[field.value]"
              :target-model="field.targetModel"
              :target-field="field.targetField"
              :placeholder="`Выберите ${field.label.toLowerCase()}`"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-reference-item'"
            />

            <!-- Выбор путевого листа -->
            <WaybillSelect
              v-model="searchValues[field.value]"
              placeholder="Выберите путевой лист"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-waybill'"
            />

            <!-- Выбор типа ресурса журнала событий -->
            <ActivityLogResourceSelect
              v-model="searchValues[field.value]"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-activity-log-resource'"
            />

            <!-- Выбор действия журнала событий -->
            <ActivityLogActionSelect
              v-model="searchValues[field.value]"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-activity-log-action'"
            />

            <!-- Выбор Saby-интеграции -->
            <SabyIntegrationSelect
              v-model="searchValues[field.value]"
              :clearable="true"
              :auto-select-single="false"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-saby-integration'"
            />

            <!-- Выбор типа ЭДО-документа -->
            <EdoDocumentTypeSelect
              v-model="searchValues[field.value]"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'select-edo-document-type'"
            />

            <!--
              Чекбокс-фильтр.

              Поддерживает необязательное поле queryMapping в конфиге:
                "queryMapping": {
                  "checkedValue": { "apiKey": "apiValue", ... }
                }

              Когда чекбокс отмечен - все пары из checkedValue добавляются
              в q[...] параметры запроса. Когда снят - не добавляются ничего.

              Пример в JSON-конфиге фильтров:
                {
                  "type": "checkbox",
                  "label": "Только из банковских выписок",
                  "value": "bank_statement_only",
                  "queryMapping": {
                    "checkedValue": { "source_type": "bank_statement" }
                  }
                }
            -->
            <base-checkbox
              v-model="searchValues[field.value]"
              @update:modelValue="scheduleAutoSearch"
              v-else-if="field.type == 'checkbox'"
            >
              {{ field.label }}
            </base-checkbox>
          </div>
        </div>

        <div
          v-if="inactiveCheckboxIsVisible"
          class="filter-group checkbox-group"
        >
          <base-checkbox
            v-model="showInactive"
            class="inactive-checkbox"
            @update:modelValue="scheduleAutoSearch"
          >
            {{ showInactiveLabel }}
          </base-checkbox>
        </div>

        <div class="buttons-group">
          <base-primary-button
            v-if="!autoSearch"
            type="button"
            @click="handleSearch"
            class="search-btn"
          >
            Поиск
          </base-primary-button>

          <base-button
            type="button"
            variant="secondary"
            @click="handleReset"
            class="reset-btn"
            :disabled="!hasActiveFilters"
          >
            Сбросить
          </base-button>

          <!-- Кнопка сохранить -->
          <base-button
            v-if="targetType"
            type="button"
            variant="secondary"
            @click="openSaveModal"
            class="save-filter-btn"
            :disabled="!hasActiveFilters"
          >
            Сохранить набор фильтров
          </base-button>
        </div>
      </div>
    </transition>

    <!-- Модалка сохранения фильтра -->
    <Teleport to="body">
      <SaveFilterModal
        v-if="showSaveModal"
        @close="showSaveModal = false"
        @save="handleSaveFilter"
      />
    </Teleport>

    <!-- Диалог подтверждения удаления -->
    <Teleport to="body">
      <base-dialog
        :visible="!!filterToDelete"
        title="Удалить фильтр?"
        :message="
          filterToDelete
            ? `Фильтр «${filterToDelete.name}» будет удалён безвозвратно.`
            : ''
        "
        :actions="deleteDialogActions"
        :close-on-overlay="true"
        @action="handleDeleteDialogAction"
      />
    </Teleport>
  </div>
</template>

<script>
import CounterpartySelect from "@/components/Counterparty/CounterpartySelect.vue";
import DriverSelect from "@/components/Driver/DriverSelect.vue";
import VehicleSelect from "@/components/Vehicle/VehicleSelect.vue";
import FleetParkSelect from "@/components/Vehicle/FleetParkSelect.vue";
import CompanyUserSelect from "@/components/CompanyUser/CompanyUserSelect.vue";
import ReferenceItemSelect from "@/components/ReferenceItem/ReferenceItemSelect.vue";
import ExpenseCategorySelect from "@/components/Expense/ExpenseCategorySelect.vue";
import PaymentSourceSelect from "@/components/Expense/PaymentSourceSelect.vue";
import WaybillSelect from "@/components/Waybill/WaybillSelect.vue";
import SaveFilterModal from "@/components/Filter/SaveFilterModal.vue";
import ActivityLogResourceSelect from "@/components/ActivityLog/ActivityLogResourceSelect.vue";
import ActivityLogActionSelect from "@/components/ActivityLog/ActivityLogActionSelect.vue";
import SabyIntegrationSelect from "@/components/Edo/SabyIntegrationSelect.vue";
import EdoDocumentTypeSelect from "@/components/Edo/DocumentTypeSelect.vue";

import savedFiltersApi from "@/services/api/savedFiltersApi";

import { useNotificationStore } from "@/stores/notificationStore";

import { getCompanyId } from "@/utils/userUtils";
import {
  saveActiveFilterToStorage,
  clearStoredActiveFilter,
  getStoredActiveFilterId,
} from "@/utils/filterUtils";

export default {
  name: "FilterBlock",
  components: {
    CounterpartySelect,
    DriverSelect,
    VehicleSelect,
    FleetParkSelect,
    CompanyUserSelect,
    ReferenceItemSelect,
    ExpenseCategorySelect,
    PaymentSourceSelect,
    WaybillSelect,
    SaveFilterModal,
    ActivityLogResourceSelect,
    ActivityLogActionSelect,
    SabyIntegrationSelect,
    EdoDocumentTypeSelect,
  },
  emits: ["filter-change"],
  props: {
    toggleLabel: {
      type: String,
      default: "Поиск",
    },
    searchFields: {
      type: Array,
      required: true,
      default: () => [],
    },
    // Примененные фильтры не раскрывают поиск автоматически
    initialCollapsed: {
      type: Boolean,
      default: true,
    },
    initialFilters: {
      type: Object,
      default: () => ({
        searchValues: {},
        showInactive: false,
      }),
    },
    // Тип модели для сохранённых фильтров (например, "Order", "Trip").
    // Если не передан, то функционал сохранения фильтров недоступен.
    targetType: {
      type: String,
      default: null,
    },
    autoSearch: {
      type: Boolean,
      default: false,
    },
    // Задержка debounce в мс для авто-поиска
    autoSearchDelay: {
      type: Number,
      default: 400,
    },
    // Если false — скрывает чекбокс "Показывать неактивные"
    showInactiveCheckbox: {
      type: Boolean,
      default: true,
    },
    showInactiveLabel: {
      type: String,
      default: "Показывать неактивные",
    },
    // Дополнительный отступ после блока. По умолчанию используется CSS-значение.
    bottomMargin: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    const initialSearchValues = {};
    this.searchFields.forEach((field) => {
      if (field.type === "checkbox") {
        initialSearchValues[field.value] =
          this.initialFilters.searchValues[field.value] || false;
      } else {
        initialSearchValues[field.value] =
          this.initialFilters.searchValues[field.value] ?? "";
      }
    });

    return {
      companyId: getCompanyId(),

      searchValues: initialSearchValues,
      showInactive: this.initialFilters.showInactive || false,
      isCollapsed: this.initialCollapsed,

      savedFilters: [],
      activeFilterId: null,
      showSaveModal: false,

      filterToDelete: null,

      autoSearchTimer: null,

      deleteDialogActions: [
        {
          key: "confirm",
          label: "Удалить",
          type: "danger",
          class: "delete-btn",
        },
        {
          key: "close",
          label: "Отмена",
          type: "secondary",
        },
      ],
    };
  },
  computed: {
    selectedFilterCount() {
      return this.countSelectedFilters(this.currentFilters);
    },

    notificationStore() {
      return useNotificationStore();
    },

    hasActiveFilters() {
      const hasSearchValues = Object.values(this.searchValues).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          value !== "" &&
          value !== false
      );
      return (
        hasSearchValues ||
        (this.inactiveCheckboxIsVisible && this.showInactive)
      );
    },

    // Собирает дополнительные q-параметры из полей типа checkbox с queryMapping.
    // Если чекбокс отмечен и у поля есть queryMapping.checkedValue -
    // все пары из checkedValue попадут в extraQueryParams и будут переданы
    // в filter-change, откуда страница добавит их в q[...] запроса.
    checkboxQueryParams() {
      const result = {};
      this.searchFields.forEach((field) => {
        if (
          field.type === "checkbox" &&
          field.queryMapping?.checkedValue &&
          this.searchValues[field.value] === true
        ) {
          Object.assign(result, field.queryMapping.checkedValue);
        }
      });
      return result;
    },

    currentFilters() {
      return {
        searchValues: { ...this.searchValues },
        showInactive: this.inactiveCheckboxIsVisible
          ? this.showInactive
          : null,
        extraQueryParams: this.checkboxQueryParams,
      };
    },

    matchingFilterId() {
      const storedId = getStoredActiveFilterId(this.targetType);
      const filters = [...this.savedFilters].sort((a, b) => Number(b.id === storedId) - Number(a.id === storedId));
      for (const filter of filters) {
        const { searchValues, showInactive } = filter.params;

        const currentKeys = Object.keys(this.searchValues);
        const allMatch = currentKeys.every((key) => {
          const current = this.searchValues[key] ?? "";
          const saved = searchValues?.[key] ?? "";
          return Array.isArray(current) || Array.isArray(saved)
            ? JSON.stringify(current) === JSON.stringify(saved)
            : String(current) === String(saved);
        });

        // Если чекбокс скрыт, то не учитываем поле showInactive при сравнении
        const inactiveMatch =
          !this.inactiveCheckboxIsVisible ||
          (this.showInactive || false) === (showInactive || false);

        if (allMatch && inactiveMatch) return filter.id;
      }
      return null;
    },

    inactiveCheckboxIsVisible() {
      return (
        this.showInactiveCheckbox &&
        this.initialFilters.showInactive !== undefined
      );
    },
  },
  watch: {
    searchFields: {
      handler(newFields) {
        const newSearchValues = {};
        newFields.forEach((field) => {
          if (field.type === "checkbox") {
            newSearchValues[field.value] =
              this.searchValues[field.value] ?? false;
          } else {
            newSearchValues[field.value] =
              this.searchValues[field.value] || "";
          }
        });
        this.searchValues = newSearchValues;
      },
      immediate: true,
    },
    initialFilters: {
      handler(newFilters) {
        const newSearchValues = {};
        this.searchFields.forEach((field) => {
          if (field.type === "checkbox") {
            newSearchValues[field.value] =
              newFilters.searchValues[field.value] || false;
          } else {
            newSearchValues[field.value] =
              newFilters.searchValues[field.value] ?? "";
          }
        });
        this.searchValues = newSearchValues;
        this.showInactive = newFilters.showInactive || false;
      },
      deep: true,
      immediate: true,
    },
  },
  beforeUnmount() {
    clearTimeout(this.autoSearchTimer);
  },
  methods: {
    countSelectedFilters({ searchValues = {}, showInactive = false }) {
      const hasValue = (value) => {
        if (Array.isArray(value)) return value.some(hasValue);
        if (value && typeof value === "object") return Object.values(value).some(hasValue);
        if (typeof value === "string") return value.trim() !== "";
        return value !== null && value !== undefined && value !== false;
      };
      const count = this.searchFields.filter((field) => hasValue(searchValues[field.value])).length;
      const hasArchive = this.showInactiveCheckbox && showInactive;
      return count + (hasArchive ? 1 : 0);
    },

    toggleFilters() {
      this.isCollapsed = !this.isCollapsed;
    },

    scheduleAutoSearch() {
      if (!this.autoSearch) return;
      clearTimeout(this.autoSearchTimer);
      this.autoSearchTimer = setTimeout(() => {
        this.handleSearch();
      }, this.autoSearchDelay);
    },

    handleFleetParkFilterUpdate(field) {
      this.searchFields.forEach((searchField) => {
        if (searchField.fleetParkField === field.value) {
          this.searchValues[searchField.value] = "";
        }
      });

      this.scheduleAutoSearch();
    },

    handleSearch() {
      const matched = this.matchingFilterId;
      this.activeFilterId = matched;

      if (this.targetType) {
        if (matched) {
          saveActiveFilterToStorage(this.targetType, matched);
        } else {
          clearStoredActiveFilter(this.targetType);
        }
      }

      this.$emit("filter-change", { ...this.currentFilters });
    },

    handleReset() {
      clearTimeout(this.autoSearchTimer);

      const resetValues = {};
      this.searchFields.forEach((field) => {
        resetValues[field.value] =
          field.type === "checkbox" ? false : "";
      });
      this.searchValues = resetValues;
      this.showInactive = false;
      this.activeFilterId = null;

      if (this.targetType) {
        clearStoredActiveFilter(this.targetType);
      }

      this.$emit("filter-change", {
        searchValues: {},
        showInactive: this.inactiveCheckboxIsVisible ? false : null,
        extraQueryParams: {},
      });
    },

    setSearch(fieldValues) {
      this.searchValues = { ...this.searchValues, ...fieldValues };
      this.handleSearch();
    },

    resetFilters() {
      this.handleReset();
    },

    async fetchSavedFilters() {
      try {
        const response = await savedFiltersApi.index(this.companyId, {
          "q[target_type]": this.targetType,
        });
        this.savedFilters = response.data.result || [];
        this.activeFilterId = this.matchingFilterId;
      } catch (error) {
        console.error(
          "Ошибка при загрузке сохранённых фильтров",
          error
        );
      }
    },

    async loadStoredFilter() {
      if (!this.targetType) return null;

      const storedId = getStoredActiveFilterId(this.targetType);

      await this.fetchSavedFilters();

      if (!storedId) return null;

      const filter = this.savedFilters.find((f) => f.id === storedId);

      if (!filter) {
        clearStoredActiveFilter(this.targetType);
        return null;
      }

      const { searchValues, showInactive } = filter.params;

      const newSearchValues = {};
      this.searchFields.forEach((field) => {
        if (field.type === "checkbox") {
          newSearchValues[field.value] =
            searchValues?.[field.value] || false;
        } else {
          newSearchValues[field.value] =
            searchValues?.[field.value] || "";
        }
      });

      this.searchValues = newSearchValues;
      this.showInactive = showInactive || false;
      this.activeFilterId = filter.id;
      this.isCollapsed = true;

      return { ...this.currentFilters };
    },

    openSaveModal() {
      this.showSaveModal = true;
    },

    async handleSaveFilter(name) {
      try {
        const params = {
          target_type: this.targetType,
          name,
          params: {
            searchValues: { ...this.searchValues },
            showInactive: this.inactiveCheckboxIsVisible
              ? this.showInactive
              : null,
          },
        };

        const response = await savedFiltersApi.create(
          this.companyId,
          params
        );
        const created = response.data.result;

        this.savedFilters.unshift(created);

        this.notificationStore.showSuccess("Фильтр успешно сохранён");
        this.showSaveModal = false;
      } catch (error) {
        console.error("Ошибка при сохранении фильтра", error);
        this.showSaveModal = false;
      }
    },

    applySavedFilter(filter) {
      const { searchValues, showInactive } = filter.params;

      const newSearchValues = {};
      this.searchFields.forEach((field) => {
        if (field.type === "checkbox") {
          newSearchValues[field.value] =
            searchValues?.[field.value] || false;
        } else {
          newSearchValues[field.value] =
            searchValues?.[field.value] || "";
        }
      });

      this.searchValues = newSearchValues;
      this.showInactive = showInactive || false;
      this.activeFilterId = filter.id;

      saveActiveFilterToStorage(this.targetType, filter.id);
      this.$emit("filter-change", { ...this.currentFilters });
    },

    confirmDeleteFilter(filter) {
      this.filterToDelete = filter;
    },

    handleDeleteDialogAction(actionKey) {
      if (actionKey === "confirm") {
        this.deleteSavedFilter(this.filterToDelete.id);
      }
      this.filterToDelete = null;
    },

    async deleteSavedFilter(id) {
      try {
        await savedFiltersApi.delete(this.companyId, id);

        this.savedFilters = this.savedFilters.filter(
          (f) => f.id !== id
        );

        if (this.activeFilterId === id) {
          this.handleReset();
        }

        this.notificationStore.showSuccess("Фильтр удалён");
      } catch (error) {
        console.error("Ошибка при удалении фильтра", error);
      }
    },
  },
};
</script>

<style scoped>
.filter-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-header-toggle {
  width: min-content;
  cursor: pointer;
}

.search-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  height: 32px;
  padding: 0 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  border: 1px solid var(--color-dark);
  border-radius: 30px;
  cursor: pointer;
  gap: 6px;
  box-sizing: border-box;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

.search-button:hover {
  background: #e5e9fd;
}

.search-button.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.search-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.search-filter-count {
  position: relative;
  top: 1px;
  left: 1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  box-sizing: border-box;
  border-radius: 8px;
  background: #eef2f6;
  color: #64748b;
  font-size: 10px;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.search-button.active .search-filter-count {
  background: rgb(255 255 255 / 15%);
  color: rgb(255 255 255 / 85%);
}

.search-icon {
  font-size: 15px;
  line-height: 1;
}

.search-arrow {
  margin-left: 2px;
}

/* Сохранённые фильтры */
.saved-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.saved-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  min-height: 28px;
  padding: 0 8px 0 10px;
  border: 1px solid var(--color-grey);
  border-radius: 30px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  color: var(--color-dark);
  white-space: nowrap;
  box-sizing: border-box;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.saved-filter-chip:hover {
  border-color: var(--color-blue);
  background: #f0f3ff;
}

.saved-filter-chip.active {
  border-color: var(--color-blue);
  background: #e8edff;
  color: var(--color-blue);
}

.chip-name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 10px;
  color: #999;
  line-height: 1;
  padding: 0;
  border-radius: 50%;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.chip-delete:hover {
  color: var(--color-danger);
  background-color: #fde8e8;
}

.reset-active-filter-btn {
  height: 28px;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid var(--color-danger);
  border-radius: 30px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  color: var(--color-danger);
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.reset-active-filter-btn:hover {
  background-color: var(--color-danger);
  color: #fff;
}

.filter-container {
  margin-bottom: var(--base-out-indent);
}

.filter-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  margin-bottom: 0;
}

.search-fields-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-group--checkbox {
  justify-content: flex-end;
  padding-bottom: 8px;
}

.search-input {
  width: 100%;
}

.buttons-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-btn {
  width: 100px;
}

.reset-btn {
  white-space: nowrap;
  min-width: 90px;
}

.save-filter-btn {
  white-space: nowrap;
}

.checkbox-group {
  margin-top: 0;
  width: min-content;
}

.inactive-checkbox {
  white-space: nowrap;
}

@media (max-width: 1400px) {
  .search-fields-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filter-group--checkbox {
    padding-bottom: 0;
  }
}

@media (max-width: 1024px) {
  .search-fields-grid {
    grid-template-columns: 1fr;
  }

  .buttons-group {
    justify-content: stretch;
  }

  .search-btn,
  .reset-btn {
    flex: 1;
  }
}

@media (max-width: 480px) {
  .filter-content {
    gap: 10px;
    padding: 12px;
  }

  .search-fields-grid {
    gap: 10px;
  }

  .buttons-group {
    flex-direction: column;
  }

  .search-btn,
  .reset-btn,
  .save-filter-btn {
    width: 100%;
  }
}
</style>
